import React from 'react';
import { X, Mail, Send, CheckCheck, AlertCircle, Clock, Reply, Users, Inbox } from 'lucide-react';
import { EmailLog, GmailEmail } from '../../services/communication.service';
import { useCommunicationStore } from '../../store/communicationStore';

interface ViewEmailModalProps {
    isOpen: boolean;
    onClose: () => void;
    email?: EmailLog | undefined;
    gmailEmail?: GmailEmail | undefined;
}

const ViewEmailModal: React.FC<ViewEmailModalProps> = ({
    isOpen,
    onClose,
    email,
    gmailEmail,
}) => {
    const { updateEmailStatus, isLoading } = useCommunicationStore();

    // Determine if this is a Gmail email or a sent email log
    const isGmail = !!gmailEmail;
    const displayEmail = gmailEmail || email;

    const formatDate = (dateInput: string | Date) => {
        const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
        return date.toLocaleString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    };

    const getStatusInfo = (status: EmailLog['status']) => {
        const config: Record<string, { style: string; icon: React.ReactNode; label: string; bgColor: string }> = {
            sent: {
                style: 'text-blue-600 dark:text-blue-400',
                bgColor: 'bg-blue-100 dark:bg-blue-900/30',
                icon: <Send className="w-4 h-4" />,
                label: 'Sent',
            },
            replied: {
                style: 'text-green-600 dark:text-green-400',
                bgColor: 'bg-green-100 dark:bg-green-900/30',
                icon: <CheckCheck className="w-4 h-4" />,
                label: 'Replied',
            },
            bounced: {
                style: 'text-red-600 dark:text-red-400',
                bgColor: 'bg-red-100 dark:bg-red-900/30',
                icon: <AlertCircle className="w-4 h-4" />,
                label: 'Bounced',
            },
            scheduled: {
                style: 'text-orange-600 dark:text-orange-400',
                bgColor: 'bg-orange-100 dark:bg-orange-900/30',
                icon: <Clock className="w-4 h-4" />,
                label: 'Scheduled',
            },
        };
        return config[status] || config.sent;
    };

    const handleMarkAsReplied = async () => {
        if (email) {
            await updateEmailStatus(email.id, 'replied');
        }
    };

    const statusInfo = email ? getStatusInfo(email.status) : null;

    if (!isOpen || !displayEmail) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isGmail ? 'bg-gradient-to-br from-red-500 to-pink-600' : 'bg-gradient-to-br from-primary-500 to-indigo-600'}`}>
                            {isGmail ? <Inbox className="w-5 h-5 text-white" /> : <Mail className="w-5 h-5 text-white" />}
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                {isGmail ? 'Gmail Inbox' : 'Email Details'}
                            </h2>
                            {statusInfo && (
                                <div className={`flex items-center gap-1.5 text-sm ${statusInfo.style}`}>
                                    {statusInfo.icon}
                                    <span>{statusInfo.label}</span>
                                </div>
                            )}
                            {isGmail && (
                                <div className="flex items-center gap-1.5 text-sm text-red-500 dark:text-red-400">
                                    <Inbox className="w-4 h-4" />
                                    <span>Received</span>
                                </div>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Email Header Info */}
                    <div className="space-y-3">
                        {isGmail && gmailEmail && (
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">From</p>
                                    <p className="text-gray-900 dark:text-white font-medium">
                                        {gmailEmail.from}
                                    </p>
                                </div>
                            </div>
                        )}

                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm text-gray-500 dark:text-gray-400">To</p>
                                <p className="text-gray-900 dark:text-white font-medium">
                                    {isGmail && gmailEmail ? gmailEmail.to.join(', ') : email?.to.join(', ')}
                                </p>
                            </div>
                            {!isGmail && email?.partnership && (
                                <div className="flex items-center gap-2 px-3 py-1.5 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                                    <Users className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                                    <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                                        {email.partnership.brand}
                                    </span>
                                </div>
                            )}
                        </div>

                        {!isGmail && email?.cc && email.cc.length > 0 && (
                            <div>
                                <p className="text-sm text-gray-500 dark:text-gray-400">CC</p>
                                <p className="text-gray-900 dark:text-white">{email.cc.join(', ')}</p>
                            </div>
                        )}

                        <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Subject</p>
                            <p className="text-gray-900 dark:text-white font-semibold text-lg">
                                {isGmail && gmailEmail ? (gmailEmail.subject || '(No subject)') : email?.subject}
                            </p>
                        </div>

                        <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                {isGmail ? 'Received' : 'Sent'}
                            </p>
                            <p className="text-gray-900 dark:text-white">
                                {isGmail && gmailEmail ? formatDate(gmailEmail.date) : email && formatDate(email.sentAt)}
                            </p>
                        </div>
                    </div>

                    {/* Status Timeline - Only for sent emails */}
                    {!isGmail && email && statusInfo && (
                        <div className={`${statusInfo.bgColor} rounded-xl p-4`}>
                            <h4 className={`text-sm font-medium ${statusInfo.style} mb-3`}>Status Timeline</h4>
                            <div className="space-y-2">
                                <div className="flex items-center gap-3 text-sm">
                                    <Send className="w-4 h-4 text-blue-500" />
                                    <span className="text-gray-700 dark:text-gray-300">Sent: {formatDate(email.sentAt)}</span>
                                </div>
                                {email.repliedAt && (
                                    <div className="flex items-center gap-3 text-sm">
                                        <CheckCheck className="w-4 h-4 text-green-500" />
                                        <span className="text-gray-700 dark:text-gray-300">Replied: {formatDate(email.repliedAt)}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Gmail Labels */}
                    {isGmail && gmailEmail && gmailEmail.labels.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {gmailEmail.labels.map((label) => (
                                <span
                                    key={label}
                                    className="px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-lg"
                                >
                                    {label}
                                </span>
                            ))}
                        </div>
                    )}

                    {/* Email Body / Snippet */}
                    <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                            {isGmail ? 'Preview' : 'Message'}
                        </p>
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 prose prose-sm dark:prose-invert max-w-none">
                            <pre className="whitespace-pre-wrap font-sans text-gray-700 dark:text-gray-300">
                                {isGmail && gmailEmail ? gmailEmail.snippet : email?.bodyHtml}
                            </pre>
                        </div>
                    </div>

                    {/* Template Info - Only for sent emails */}
                    {!isGmail && email?.template && (
                        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                            <span>Template used:</span>
                            <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-lg font-medium">
                                {email.template.name}
                            </span>
                        </div>
                    )}
                </div>

                {/* Footer Actions */}
                <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl transition-colors"
                    >
                        Close
                    </button>
                    <div className="flex items-center gap-2">
                        {!isGmail && email && email.status !== 'replied' && (
                            <button
                                onClick={handleMarkAsReplied}
                                disabled={isLoading}
                                className="flex items-center gap-2 px-4 py-2 text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50 rounded-xl transition-colors"
                            >
                                <Reply className="w-4 h-4" />
                                Mark as Replied
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewEmailModal;
